import ValidateModule from "./ValidateModule";

import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { IEventChannel, ApiOpts } from "../model/Models";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class ThematicModule
 */
export default class ThematicModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    pendingThematicFromServerDfd: object;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.pendingThematicFromServerDfd = {};

        this.getOrCreateThematicFromCacheOrServer = this.getOrCreateThematicFromCacheOrServer.bind(this);
        this.getColorThematic = this.getColorThematic.bind(this);
        this.getSizeThematic = this.getSizeThematic.bind(this);
        this.getSymbolThematic = this.getSymbolThematic.bind(this);
        this.recalculateThematic = this.recalculateThematic.bind(this);
        this.recalculateColorThematic = this.recalculateColorThematic.bind(this);
        this.recalculateSizeThematic = this.recalculateSizeThematic.bind(this);
        this.recalculateSymbolThematic = this.recalculateSymbolThematic.bind(this);
        this.update = this.update.bind(this);
    }

    /**
     * Fetches a thematic (if enabled) from JS cache or from server.
     * 
     * @param thematicType 
     * @param layerViewId 
     * @param attrName 
     * @param opts 
     * 
     * @returns 
     */
    private getOrCreateThematicFromCacheOrServer(thematicType: string, layerViewId: number, attrName: string, opts?: ApiOpts) {
        //console.log("getOrCreateThematicFromCacheOrServer", thematicType, layerViewId, attrName, opts);
        
        if (!ValidateModule.checkId(layerViewId) ||
            !ValidateModule.checkMandatoryArg(attrName)) {
            return es.util.deferred.rejected();
        }
        
        opts = $.extend({}, opts); // ensure opts is defined
        
        let url = es.api.getURL(this.resourceName) + '/' + thematicType + '/' + layerViewId + '/' + attrName;
        if (typeof opts.persistThematic == 'boolean' && !opts.persistThematic) {
            opts.noCache = true;
            url += '?persist=false';
            
            return this.rqBroker.doPut({'url': url, 'opts': opts});
        }
        
        var cacheThematicDfd = es.api._cache().get('Thematic_GET_' + thematicType, layerViewId + '_' + attrName);
        if (cacheThematicDfd) {
            return cacheThematicDfd;
        }

        if (this.pendingThematicFromServerDfd[thematicType + '_' + layerViewId + '_' + attrName]) {
            return this.pendingThematicFromServerDfd[thematicType + '_' + layerViewId + '_' + attrName];
        }

        url = es.api.getURL(this.resourceName) + '/' + thematicType + '/' + layerViewId + '/' + attrName;

        return this.pendingThematicFromServerDfd[thematicType + '_' + layerViewId + '_' + attrName] =  this.rqBroker.doPut({
            url: url,
            opts: opts
        }).done((rs) => {
            this.cache.set(this.resourceName+ "_GET_" + thematicType, layerViewId + '_' + attrName, rs, opts);
        }).always(() => {
            delete this.pendingThematicFromServerDfd[thematicType + '_' + layerViewId + '_' + attrName];
        });
    }

    /**
     * @function es.module.Thematic.getColorThematic
     * Retrieves color thematic for a given attribute
     *
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts persistThematic can be specified to indicate if server should persist the thematic in case it wasn't yet created. Options are passed on
     *        to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    getColorThematic(layerViewId: number, attrName: string, opts?: ApiOpts) {
        return this.getOrCreateThematicFromCacheOrServer('COLOR', layerViewId, attrName, opts);
    }

    /**
     * @function es.module.Thematic.getSizeThematic
     * Retrieves size thematic for a given attribute
     *
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts persistThematic can be specified to indicate if server should persist the thematic in case it wasn't yet created. Options are passed on
     *        to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    getSizeThematic(layerViewId: number, attrName: string, opts?: ApiOpts) {
        return this.getOrCreateThematicFromCacheOrServer('SIZE', layerViewId, attrName, opts);
    }

    /**
     * @function es.module.Thematic.getSymbolThematic
     * Retrieves symbol thematic for a given attribute
     *
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts options passed on to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    getSymbolThematic(layerViewId: number, attrName: string, opts?: ApiOpts) {
        return this.getOrCreateThematicFromCacheOrServer('SYMBOL', layerViewId, attrName, opts);
    }
    
    /**
     * @function recalculateThematic
     * Retrieves default thematic, of a given type, for a given attribute. This thematic is NOT persisted in server side.
     *
     * @param thematicType thematic type
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts options passed on to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    private recalculateThematic(thematicType: string, layerViewId: number, attrName: string, opts?: ApiOpts) {
        if (!ValidateModule.checkId(layerViewId) ||
            !ValidateModule.checkMandatoryArg(attrName)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/' + thematicType + '/' + layerViewId + '/' + attrName + '?recalculate=true&persist=false';

        return this.rqBroker.doPut({'url': url, opts: opts});
    }

    /**
     * @function es.module.Thematic.recalculateColorThematic
     * Retrieves default color thematic for a given attribute. This thematic is NOT persisted in server side.
     *
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts options passed on to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    recalculateColorThematic(layerViewId: number, attrName: string, opts?: ApiOpts) {
        return this.recalculateThematic('COLOR', layerViewId, attrName, opts);
    }

    /**
     * @function es.module.Thematic.recalculateSizeThematic
     * Retrieves default size thematic for a given attribute. This thematic is NOT persisted in server side.
     *
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts options passed on to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    recalculateSizeThematic(layerViewId: number, attrName: string, opts?: ApiOpts) {
        return this.recalculateThematic('SIZE', layerViewId, attrName, opts);
    }

    /**
     * @function es.module.Thematic.recalculateSymbolThematic
     * Retrieves default symbol thematic for a given attribute. This thematic is NOT persisted in server side.
     *
     * @param layerViewId layerView id
     * @param attrName attribute name
     * @param opts options passed on to the request broker.
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    recalculateSymbolThematic(layerViewId: number, attrName: string, opts?: ApiOpts) {
        return this.recalculateThematic('SYMBOL', layerViewId, attrName, opts);
    }

    /**
     * @function es.module.Thematic.update
     * Update an existing thematic on the server
     *
     * @param resourceObj thematic
     * @param opts
     *
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        opts = $.extend(undefined, {resolveWithXHR: true});
        
        const url = es.api.getURL(this.resourceName) + '/' + getHintPath(resourceObj.$type) + '/' + resourceObj.layerViewId + '/' + resourceObj.attribute;

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: resourceObj
        }).then((rs, jqXHR) => {
            this.cache.set(this.resourceName+ "_GET_" + getHintPath(resourceObj.$type), resourceObj.layerViewId + '_' + resourceObj.attribute, rs, opts);

            var geoMapLastModified = jqXHR.getResponseHeader("es_geomap_last_modified");
            if (!geoMapLastModified) {
                this.eventChannel.fireEvent("onUpdateThematic", rs);
                return rs;
            }
            
            geoMapLastModified = parseInt(geoMapLastModified);
            
            this.cache.clear("LayerView", rs.layerViewId);
            this.cache.clear("LayerInfo_LayerView", rs.layerViewId);
            
            return Promise.all([
                es.api.layerView.get(rs.layerViewId),
                es.api.layerInfo.getForLayerView(rs.layerViewId)
            ]).then(([layerView, layerInfo]) => {
                return es.api.appResources.setWorkingCopyChanged(layerView.geoMapId).then((geoMap) => {
                    this.eventChannel.fireEvent("onUpdateThematic", rs);
                    this.eventChannel.fireEvent("onUpdateLayerView", layerView, layerInfo, geoMapLastModified);

                    return rs;
                });
            });
        });
    }
}

function getHintPath(themType: string) {
    if (themType.endsWith('ColorThematic')) {
        return 'COLOR';
    }

    if (themType.endsWith('SizeThematic')) {
        return 'SIZE';
    }

    if (themType.endsWith('SymbolThematic')) {
        return 'SYMBOL';
    }

    return 'UNKNOWN';
}
